// Inline attribute editors for an entity's role rows, reused by both the
// company (OrganizationDetailPage) and individual (PersonDetailPage) pages so
// you can see/edit a Client / Vendor / Contractor role wherever you reach the
// entity. Each loads its own role row by id and PATCHes via the role's hook.
// Engagement lists (invoices/retainers/projects) are linked, not embedded.
//
// Roles can also be *removed* (soft-delete the role row — person/org stays)
// and *assigned* via <AssignRolesSection> for roles the entity does not yet hold.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useClient, useUpdateClient, useDeleteClient } from '../api/queries/clients';
import { useVendor, useUpdateVendor, useDeleteVendor } from '../api/queries/vendors';
import { useContractor, useUpdateContractor, useDeleteContractor } from '../api/queries/contractors';
import { useLookups, activeOptionsFor, useLookupLabel } from '../api/queries/lookups';
import { LookupSelect } from './LookupSelect';
import { IconAction } from './IconAction';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { AddressFields } from './AddressFields';
import {
  addressValueFromColumns,
  addressValueToColumns,
  formatAddressColumns,
  emptyAddressValue,
  type AddressValue,
} from '../utils/address';
import type { ClientStatus, RoleBadge } from '@stfors/shared-types';
import { useDirectoryRoleOptions } from './RolePills';
import { OrgPicker } from './OrgPicker';
import './RoleEditors.css';

function MetaItem({ label, value }: { label: string; value: string | null }): JSX.Element | null {
  if (!value) return null;
  return (
    <div>
      <div className="org-meta-label">{label}</div>
      <div className="org-meta-value" style={{ whiteSpace: 'pre-wrap' }}>{value}</div>
    </div>
  );
}

// ---------- Client ----------

export function ClientRoleEditor({ clientId }: { clientId: number }): JSX.Element {
  const q = useClient(clientId);
  const update = useUpdateClient(clientId);
  const remove = useDeleteClient();
  const canWrite = can('client.write');
  const canDelete = can('client.delete');
  const statusLabel = useLookupLabel('client_status');
  const roleLabel = useLookupLabel('contact_role');
  const clientRoleLabel = roleLabel('client') || 'Client';
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState<ClientStatus>('active');
  const [email, setEmail] = useState('');
  const [addr, setAddr] = useState<AddressValue>(emptyAddressValue());
  const [tagsRaw, setTagsRaw] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const c = q.data?.client;
  // For a company client, email + address live on the linked organization (the
  // single source of truth, edited on the client detail page / company page) —
  // so they're hidden here to avoid a second, competing pair of fields.
  const isCompany = c?.kind === 'company';

  const startEdit = () => {
    if (!c) return;
    setStatus(c.status);
    setEmail(c.email ?? '');
    setAddr(addressValueFromColumns(c));
    setTagsRaw(c.tags.join(', '));
    setNotes(c.notes ?? '');
    setError(null);
    setEditing(true);
  };

  const onSave = async () => {
    setError(null);
    try {
      const cols = addressValueToColumns(addr);
      await update.mutateAsync({
        status,
        // Only individuals write email/address here (company → organization).
        ...(isCompany ? {} : {
          email: email.trim() || null,
          address_line1: cols.line1, address_line2: cols.line2, city: cols.city,
          state: cols.state, postal_code: cols.postal_code, country: cols.country,
        }),
        tags: tagsRaw.split(',').map((t) => t.trim()).filter(Boolean),
        notes: notes.trim() || null,
      });
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save.');
    }
  };

  const onRemove = async () => {
    setError(null);
    if (!window.confirm(
      `Remove the ${clientRoleLabel} role?\n\n`
      + 'This does not delete the person or company — only the client classification. '
      + 'Past invoices and projects stay linked to this client record (soft-deleted).',
    )) return;
    try {
      await remove.mutateAsync(clientId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `Could not remove the ${clientRoleLabel} role.`);
    }
  };

  return (
    <section className="role-editor">
      <div className="org-section-header">
        <h3><span className="role-badge role-client">{clientRoleLabel}</span></h3>
        <div className="role-editor-actions">
          {canWrite && !editing && c && (
            <IconAction action="edit" onClick={startEdit} disabled={remove.isPending} iconSize={16} />
          )}
          {canDelete && !editing && c && (
            <IconAction
              action="delete"
              label={remove.isPending ? 'Removing…' : 'Remove role'}
              onClick={onRemove}
              disabled={remove.isPending}
              iconSize={16}
            />
          )}
        </div>
      </div>
      {q.isPending ? (
        <p className="org-meta-label">Loading…</p>
      ) : !c ? (
        <p className="org-meta-label">Could not load client details.</p>
      ) : editing ? (
        <div className="new-client-form" style={{ padding: 0 }}>
          <div className="new-client-row">
            <label className="new-client-field"><span>Status</span>
              <LookupSelect category="client_status" value={status} onChange={setStatus} className="form-input" aria-label="Status" />
            </label>
            {!isCompany && (
              <label className="new-client-field"><span>Email</span>
                <input className="form-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={254} />
              </label>
            )}
          </div>
          {!isCompany && (
            <div className="new-client-field new-client-field-full"><span>Address</span>
              <AddressFields value={addr} onChange={setAddr} legacyText={c?.address} autoParse />
            </div>
          )}
          <label className="new-client-field new-client-field-full"><span>Tags (comma-separated)</span>
            <input className="form-input" value={tagsRaw} onChange={(e) => setTagsRaw(e.target.value)} />
          </label>
          <label className="new-client-field new-client-field-full"><span>Notes</span>
            <textarea className="form-input new-client-textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={10_000} />
          </label>
          {error && <p className="new-client-error" role="alert">{error}</p>}
          <div className="new-client-actions">
            <IconAction action="cancel" onClick={() => setEditing(false)} disabled={update.isPending} />
            <IconAction
              action="save"
              onClick={onSave}
              disabled={update.isPending}
              label={update.isPending ? 'Saving…' : 'Save'}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="org-detail-meta">
            <MetaItem label="Status" value={statusLabel(c.status)} />
            {!isCompany && <MetaItem label="Email" value={c.email} />}
            {!isCompany && <MetaItem label="Address" value={formatAddressColumns(c) ?? c.address} />}
            <MetaItem label="Tags" value={c.tags.length ? c.tags.join(', ') : null} />
            <MetaItem label="Notes" value={c.notes} />
          </div>
          {error && <p className="new-client-error" role="alert">{error}</p>}
          <Link to={`/clients/${clientId}`} className="role-editor-link">Invoices, retainers & projects →</Link>
        </>
      )}
    </section>
  );
}

// ---------- Vendor ----------

export function VendorRoleEditor({ vendorId }: { vendorId: number }): JSX.Element {
  const q = useVendor(vendorId);
  const update = useUpdateVendor();
  const remove = useDeleteVendor();
  // vendor.write covers create/edit/soft-delete (no separate vendor.delete).
  const canWrite = can('vendor.write');
  const lookups = useLookups();
  const taxOptions = activeOptionsFor(lookups.data?.lookup_options, 'tax_category');
  const roleLabel = useLookupLabel('contact_role');
  const vendorRoleLabel = roleLabel('vendor') || 'Vendor';
  const [editing, setEditing] = useState(false);
  const [taxCategory, setTaxCategory] = useState('');
  const [error, setError] = useState<string | null>(null);

  const v = q.data?.vendor;

  const startEdit = () => {
    if (!v) return;
    setTaxCategory(v.default_tax_category ?? '');
    setError(null);
    setEditing(true);
  };

  const onSave = async () => {
    setError(null);
    try {
      await update.mutateAsync({ id: vendorId, default_tax_category: taxCategory || null });
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save.');
    }
  };

  const onRemove = async () => {
    setError(null);
    if (!window.confirm(
      `Remove the ${vendorRoleLabel} role?\n\n`
      + 'This does not delete the person or company — only the vendor classification.',
    )) return;
    try {
      await remove.mutateAsync(vendorId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `Could not remove the ${vendorRoleLabel} role.`);
    }
  };

  return (
    <section className="role-editor">
      <div className="org-section-header">
        <h3><span className="role-badge role-vendor">{vendorRoleLabel}</span></h3>
        <div className="role-editor-actions">
          {canWrite && !editing && v && (
            <IconAction action="edit" onClick={startEdit} disabled={remove.isPending} iconSize={16} />
          )}
          {canWrite && !editing && v && (
            <IconAction
              action="delete"
              label={remove.isPending ? 'Removing…' : 'Remove role'}
              onClick={onRemove}
              disabled={remove.isPending}
              iconSize={16}
            />
          )}
        </div>
      </div>
      {q.isPending ? (
        <p className="org-meta-label">Loading…</p>
      ) : !v ? (
        <p className="org-meta-label">Could not load vendor details.</p>
      ) : editing ? (
        <div className="new-client-form" style={{ padding: 0 }}>
          <label className="new-client-field new-client-field-full"><span>Default tax category</span>
            <select className="form-input" value={taxCategory} onChange={(e) => setTaxCategory(e.target.value)}>
              <option value="">—</option>
              {taxOptions.map((o) => <option key={o.id} value={o.value}>{o.value}</option>)}
              {taxCategory && !taxOptions.some((o) => o.value === taxCategory) && <option value={taxCategory}>{taxCategory} (legacy)</option>}
            </select>
          </label>
          {error && <p className="new-client-error" role="alert">{error}</p>}
          <div className="new-client-actions">
            <IconAction action="cancel" onClick={() => setEditing(false)} disabled={update.isPending} />
            <IconAction
              action="save"
              onClick={onSave}
              disabled={update.isPending}
              label={update.isPending ? 'Saving…' : 'Save'}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="org-detail-meta">
            <MetaItem label="Default tax category" value={v.default_tax_category} />
          </div>
          {error && <p className="new-client-error" role="alert">{error}</p>}
        </>
      )}
    </section>
  );
}

// ---------- Contractor ----------

export function ContractorRoleEditor({ contractorId }: { contractorId: number }): JSX.Element {
  const q = useContractor(contractorId);
  const update = useUpdateContractor(contractorId);
  const remove = useDeleteContractor();
  const canWrite = can('contractor.write');
  const canDelete = can('contractor.delete');
  const roleLabel = useLookupLabel('contact_role');
  const contractorRoleLabel = roleLabel('contractor') || 'Contractor';
  const [editing, setEditing] = useState(false);
  const [business, setBusiness] = useState('');
  const [email, setEmail] = useState('');
  const [addr, setAddr] = useState<AddressValue>(emptyAddressValue());
  const [active, setActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const c = q.data?.contractor;

  const startEdit = () => {
    if (!c) return;
    setBusiness(c.business_name ?? '');
    setEmail(c.email ?? '');
    setAddr(addressValueFromColumns(c));
    setActive(c.is_active);
    setError(null);
    setEditing(true);
  };

  const onSave = async () => {
    setError(null);
    if (email.trim().length === 0) { setError('Email is required for a contractor.'); return; }
    try {
      const cols = addressValueToColumns(addr);
      await update.mutateAsync({
        business_name: business.trim() || null,
        email: email.trim(),
        address_line1: cols.line1, address_line2: cols.line2, city: cols.city,
        state: cols.state, postal_code: cols.postal_code, country: cols.country,
        is_active: active,
      });
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save.');
    }
  };

  const onRemove = async () => {
    setError(null);
    if (!window.confirm(
      `Remove the ${contractorRoleLabel} role?\n\n`
      + 'This does not delete the person or company — only the contractor classification.',
    )) return;
    try {
      await remove.mutateAsync(contractorId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `Could not remove the ${contractorRoleLabel} role.`);
    }
  };

  return (
    <section className="role-editor">
      <div className="org-section-header">
        <h3><span className="role-badge role-contractor">{contractorRoleLabel}</span></h3>
        <div className="role-editor-actions">
          {canWrite && !editing && c && (
            <IconAction action="edit" onClick={startEdit} disabled={remove.isPending} iconSize={16} />
          )}
          {canDelete && !editing && c && (
            <IconAction
              action="delete"
              label={remove.isPending ? 'Removing…' : 'Remove role'}
              onClick={onRemove}
              disabled={remove.isPending}
              iconSize={16}
            />
          )}
        </div>
      </div>
      {q.isPending ? (
        <p className="org-meta-label">Loading…</p>
      ) : !c ? (
        <p className="org-meta-label">Could not load contractor details.</p>
      ) : editing ? (
        <div className="new-client-form" style={{ padding: 0 }}>
          <div className="new-client-row">
            <label className="new-client-field"><span>Email *</span>
              <input className="form-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={254} />
            </label>
            <label className="new-client-field"><span>Business name</span>
              <input className="form-input" value={business} onChange={(e) => setBusiness(e.target.value)} />
            </label>
          </div>
          <div className="new-client-field new-client-field-full"><span>Address</span>
            <AddressFields value={addr} onChange={setAddr} legacyText={c?.address} autoParse />
          </div>
          <label className="new-client-field"><span>Status</span>
            <select className="form-input" value={active ? 'active' : 'inactive'} onChange={(e) => setActive(e.target.value === 'active')}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>
          {error && <p className="new-client-error" role="alert">{error}</p>}
          <div className="new-client-actions">
            <IconAction action="cancel" onClick={() => setEditing(false)} disabled={update.isPending} />
            <IconAction
              action="save"
              onClick={onSave}
              disabled={update.isPending}
              label={update.isPending ? 'Saving…' : 'Save'}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="org-detail-meta">
            <MetaItem label="Email" value={c.email} />
            <MetaItem label="Business name" value={c.business_name} />
            <MetaItem label="Address" value={formatAddressColumns(c) ?? c.address} />
            <MetaItem label="Status" value={c.is_active ? 'Active' : 'Inactive'} />
          </div>
          {error && <p className="new-client-error" role="alert">{error}</p>}
          <Link to="/contractors" className="role-editor-link">Tax ID, W-9 & system access on the Contractors page →</Link>
        </>
      )}
    </section>
  );
}

// ---------- Assign missing roles ----------

export interface AssignRolesSectionProps {
  /** Roles the entity already holds (excluded from the picker). */
  heldRoles: RoleBadge[];
  /**
   * Create the role / affiliation on the existing person/org.
   * - contractor: `email` required
   * - contact: `organizationId` required (individual only)
   */
  onAssign: (
    role: RoleBadge,
    opts?: { email?: string; organizationId?: number },
  ) => Promise<void>;
  busy?: boolean;
  /** Prefill for contractor email (e.g. person's primary email). */
  defaultEmail?: string;
  /** Company scope hides individual-only roles (Contact). Default individual. */
  scope?: 'individual' | 'company';
}

/**
 * "Add role" UI for person/company detail pages. Options come from Settings →
 * Lookups → Directory Roles (`contact_role`); only active assignable roles the
 * entity does not already hold are offered. Contractor prompts for email;
 * Contact prompts for a company.
 */
export function AssignRolesSection({
  heldRoles,
  onAssign,
  busy = false,
  defaultEmail = '',
  scope = 'individual',
}: AssignRolesSectionProps): JSX.Element | null {
  const directoryRoles = useDirectoryRoleOptions(scope);
  // Contact is multi-company: keep offering it so they can link additional orgs.
  const missing = directoryRoles.filter(
    (r) => r.value === 'contact' || !heldRoles.includes(r.value),
  );
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState<RoleBadge | null>(null);
  const [email, setEmail] = useState(defaultEmail);
  const [orgId, setOrgId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (missing.length === 0 || !can('organization.write')) return null;

  const start = (role: RoleBadge) => {
    setError(null);
    if (role === 'contractor') {
      setPicking('contractor');
      setEmail(defaultEmail);
      return;
    }
    if (role === 'contact') {
      setPicking('contact');
      setOrgId(null);
      return;
    }
    void doAssign(role);
  };

  const doAssign = async (
    role: RoleBadge,
    opts?: { email?: string; organizationId?: number },
  ) => {
    setError(null);
    try {
      await onAssign(role, opts);
      setPicking(null);
      setOrgId(null);
      setOpen(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the role.');
    }
  };

  const contractorLabel =
    directoryRoles.find((r) => r.value === 'contractor')?.label ?? 'Contractor';
  const contactLabel =
    directoryRoles.find((r) => r.value === 'contact')?.label ?? 'Contact';

  return (
    <section className="role-editor role-editor-assign">
      <div className="org-section-header">
        <h3>Roles</h3>
        {!open && (
          <button
            type="button"
            className="btn-cyan-primary"
            onClick={() => { setOpen(true); setError(null); setPicking(null); setOrgId(null); }}
            disabled={busy}
          >
            Add role
          </button>
        )}
      </div>
      <p className="org-meta-label">
        Classify this entity with a directory role (Settings → Lookups → Directory Roles).
        Contact links an individual to a company. To drop Client/Vendor/Contractor, use Remove role above.
      </p>
      {open && (
        <div className="new-client-form" style={{ padding: 0 }}>
          {picking === 'contractor' ? (
            <>
              <label className="new-client-field new-client-field-full">
                <span>{contractorLabel} email *</span>
                <input
                  className="form-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  maxLength={254}
                  autoFocus
                />
              </label>
              {error && <p className="new-client-error" role="alert">{error}</p>}
              <div className="new-client-actions">
                <button
                  type="button"
                  className="btn-charcoal-secondary"
                  onClick={() => { setPicking(null); setError(null); }}
                  disabled={busy}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn-cyan-primary"
                  disabled={busy || email.trim().length === 0}
                  onClick={() => void doAssign('contractor', { email: email.trim() })}
                >
                  {busy ? 'Adding…' : `Add ${contractorLabel}`}
                </button>
              </div>
            </>
          ) : picking === 'contact' ? (
            <>
              <div className="new-client-field new-client-field-full">
                <span className="new-client-section-label">Company *</span>
                <OrgPicker value={orgId} onChange={setOrgId} disabled={busy} />
              </div>
              {error && <p className="new-client-error" role="alert">{error}</p>}
              <div className="new-client-actions">
                <button
                  type="button"
                  className="btn-charcoal-secondary"
                  onClick={() => { setPicking(null); setOrgId(null); setError(null); }}
                  disabled={busy}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn-cyan-primary"
                  disabled={busy || orgId === null}
                  onClick={() => void doAssign('contact', { organizationId: orgId! })}
                >
                  {busy ? 'Adding…' : `Add ${contactLabel}`}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="role-assign-pills" role="group" aria-label="Directory roles to add">
                {missing.map(({ value: role, label }) => (
                  <button
                    key={role}
                    type="button"
                    className={`role-pill role-pill-${role}`}
                    onClick={() => start(role)}
                    disabled={busy}
                  >
                    + {label}
                  </button>
                ))}
              </div>
              {error && <p className="new-client-error" role="alert">{error}</p>}
              <div className="new-client-actions">
                <button
                  type="button"
                  className="btn-charcoal-secondary"
                  onClick={() => { setOpen(false); setError(null); }}
                  disabled={busy}
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
